import { useGetBuyersCertificateProductFiltersQuery } from '@api/buyersCert.api';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  BuyersCertificateFilters,
  DropdownField,
  SearchField,
  useBuyersCertificateListContext,
} from '@components';
import { useAuth } from '@hooks';
import { Clear } from '@mui/icons-material';
import { Box, Button, Grid } from '@mui/material';
import { t } from '@translations';
import { useCallback, useEffect, useMemo, useRef } from 'react';

export const BuyersCertificateListFilters = () => {
  const { clearFilters, updateFilters, filters } = useBuyersCertificateListContext();
  const { currentData: productOptions } = useGetBuyersCertificateProductFiltersQuery();
  const { isBeremaUser } = useAuth();
  const prevProductTypeRef = useRef(filters?.productType);

  const findFieldValues = useCallback(
    (filterName: keyof BuyersCertificateFilters) => {
      return filters?.[filterName] || '';
    },
    [filters]
  );

  useEffect(() => {
    if (
      prevProductTypeRef.current !== undefined &&
      prevProductTypeRef.current !== filters?.productType
    ) {
      updateFilters({ filterName: 'productModel', value: undefined });
    }
    prevProductTypeRef.current = filters?.productType;
  }, [filters?.productType, updateFilters]);

  const productTypeOptions = useMemo(() => {
    return (
      productOptions
        ?.filter((item) => item.productType)
        .map((item) => ({
          label: item.productType as string,
          value: item.productType as string,
        })) || []
    );
  }, [productOptions]);

  const productModelsOptions = useMemo(() => {
    return (
      productOptions
        ?.find((option) => option.productType === findFieldValues('productType'))
        ?.productModels?.filter((model): model is string => model !== null)
        ?.map((model) => ({ label: model, value: model })) || []
    );
  }, [productOptions, findFieldValues]);

  return (
    <Box sx={{ my: 4 }}>
      <Grid
        container
        spacing={{
          lg: 2,
          xs: 4,
        }}
        sx={{
          mb: 4,
        }}
      >
        <Grid item xs={12} md={isBeremaUser ? 12 : 6} lg={isBeremaUser ? 5 : 3}>
          <Grid
            container
            spacing={{
              lg: 2,
              xs: 4,
            }}
          >
            {isBeremaUser && (
              <Grid item xs={12} sm={6} lg={7}>
                <SearchField
                  name='dealerCustomerNo'
                  value={findFieldValues('dealerCustomerNo')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFilters({ filterName: 'dealerCustomerNo', value: e.target.value })
                  }
                  sx={{ width: '100%' }}
                  label={t(TextCodes.BuyersCertificate.Block.CustomerNo)}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={isBeremaUser ? 6 : 12} lg={isBeremaUser ? 5 : 12}>
              <SearchField
                label={t(TextCodes.BuyersCertificate.Block.CustomerMobile)}
                name='customerMobile'
                value={findFieldValues('customerMobile')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateFilters({ filterName: 'customerMobile', value: e.target.value })
                }
                sx={{ width: '100%' }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={12} lg={isBeremaUser ? 4 : 5}>
          <Grid
            container
            spacing={{
              lg: 2,
              xs: 4,
            }}
          >
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <DropdownField
                label={t(TextCodes.Warranty.Block.ProductType)}
                getValue={(value) => {
                  updateFilters({ filterName: 'productType', value: value as string });
                }}
                value={findFieldValues('productType')}
                options={productTypeOptions || []}
                withRemoveButton
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <DropdownField
                label={t(TextCodes.Warranty.Block.ProductModel)}
                getValue={(value) => {
                  updateFilters({ filterName: 'productModel', value: value as string });
                }}
                value={findFieldValues('productModel')}
                options={productModelsOptions}
                withRemoveButton
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={isBeremaUser ? 2 : 3}>
          <SearchField
            label={t(TextCodes.Warranty.Block.SerialNo)}
            name='serialNumber'
            value={findFieldValues('serialNumber')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateFilters({ filterName: 'serialNumber', value: e.target.value })
            }
            sx={{ width: '100%' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={1}>
          <Button variant='tertiary' onClick={clearFilters} startIcon={<Clear />}>
            {t(TextCodes.TechnicalMessage.Block.ClearFilters)}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
